import { useRef, useState, type DragEvent } from "react";
import { toast } from "react-toastify";

interface PdfDropZoneProps {
  onFile: (file: File) => void;
  isUploading: boolean;
  disabled?: boolean;
}

function isPdfFile(file: File): boolean {
  return file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
}

function PdfDropZone({ onFile, isUploading, disabled = false }: PdfDropZoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (disabled || isUploading) return;
    setIsDragging(true);
  };

  const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    if (disabled || isUploading) return;
    const file = event.dataTransfer.files?.[0];
    if (!file) return;
    if (!isPdfFile(file)) {
      toast.error("Please upload a PDF file");
      return;
    }
    onFile(file);
  };

  const handleClick = () => {
    if (disabled || isUploading) return;
    inputRef.current?.click();
  };

  const stateClass = isDragging
    ? "border-blue-500 bg-blue-50"
    : disabled || isUploading
      ? "border-gray-200 bg-gray-50 cursor-not-allowed"
      : "border-gray-300 bg-white hover:border-blue-400 hover:bg-blue-50/40 cursor-pointer";

  return (
    <div
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={handleClick}
      className={`rounded-lg border-2 border-dashed px-6 py-8 text-center transition-colors ${stateClass}`}
    >
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf"
        className="hidden"
        disabled={disabled || isUploading}
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) {
            if (isPdfFile(file)) {
              onFile(file);
            } else {
              toast.error("Please upload a PDF file");
            }
          }
          event.target.value = "";
        }}
      />
      {isUploading ? (
        <div className="flex items-center justify-center gap-3">
          <div className="h-5 w-5 animate-spin rounded-full border-b-2 border-blue-600" />
          <span className="text-sm font-medium text-gray-700">Extracting line items from PDF...</span>
        </div>
      ) : (
        <div>
          <p className="text-sm font-medium text-gray-700">Drop a PDF here or click to choose a file</p>
          <p className="mt-1 text-xs text-gray-500">Single PDF, up to 50MB. We will extract line items for review.</p>
        </div>
      )}
    </div>
  );
}

export default PdfDropZone;
